import { HarvestEventCode, TICK_MS } from "odyssey-shared";
import { chunkKey } from "../chunk-key.ts";
import { brokenLine, refusedLine } from "./lines.ts";
import type { HarvestMarks, PageMark } from "./marks.ts";
import type { HarvestStateEvent } from "./wire.ts";

/**
 * Harvesting on the page (ADR-142 decision 3, P2.26-T3): which of the map's harvestable props are gone for now, as the
 * server's lists and events say, hidden on the canvas through the port; the hold's cast bar; and the lines a refusal or
 * a break says. Every rule is the map server's: the page only draws what it is told. A chunk's list replaces what the
 * page held for that chunk's props; an event moves one prop; a prop the page does not know is passed over.
 */

/**
 * How long the cast bar shows its interrupted look after a break, in milliseconds.
 */
export const INTERRUPTED_MS = 1200;

/**
 * How long a prop takes to fade out when it is harvested in view, and back in when it grows back, in milliseconds. A
 * list sent as a chunk comes into view changes props at once.
 */
export const FADE_MS = 250;

/**
 * The cast bar's state: nothing, a hold running from `startedMs` to `endsMs` on the page's clock, or the interrupted
 * look until `untilMs`.
 */
export type HarvestBarState = { readonly kind: "none" } | { readonly kind: "hold"; readonly startedMs: number; readonly endsMs: number } | { readonly kind: "interrupted"; readonly untilMs: number };

/**
 * What the cast bar reads.
 */
export interface HarvestSnapshot {
  readonly bar: HarvestBarState;
}

/**
 * What the store tells a report: the indices of the props hidden now, in ascending order, and the cast bar's kind.
 */
export interface HarvestReading {
  readonly hidden: readonly number[];
  readonly bar: HarvestBarState["kind"];
}

/**
 * What the store asks of the page: the connection's `harvest` and its view's tick, the clock, the message line, and the
 * canvas's way to hide a prop or draw it again.
 */
export interface HarvestPort {
  readonly send: (index: number) => boolean;
  readonly tick: () => number;
  readonly now: () => number;
  readonly say: (line: string) => void;
  readonly withhold: (id: string, withheld: boolean, fadeMs: number) => void;
}

/**
 * The page's harvest state.
 */
export class HarvestStore {
  private snapshot: HarvestSnapshot = { bar: { kind: "none" } };
  private marks: HarvestMarks | null = null;
  private readonly hidden = new Set<number>();
  private readonly listeners = new Set<() => void>();
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  private moved = 0;
  private heldIndex = -1;

  /**
   * @param port What the store asks of the page.
   */
  public constructor(private readonly port: HarvestPort) {}

  /**
   * Listens for changes to the bar.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The current bar, the same object until it changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): HarvestSnapshot => this.snapshot;

  /**
   * A count that moves whenever a prop is hidden or drawn again, for the lookup that offers the prompt.
   *
   * @returns The count.
   */
  public get version(): number {
    return this.moved;
  }

  /**
   * Whether a hold runs now.
   *
   * @returns True while the bar shows a hold.
   */
  public get holding(): boolean {
    return this.snapshot.bar.kind === "hold";
  }

  /**
   * The marks of the booted map, which the lists and events name props by.
   *
   * @returns The marks, or null before a world loads.
   */
  public get known(): HarvestMarks | null {
    return this.marks;
  }

  /**
   * The prop the running hold names.
   *
   * @returns The prop's mark, or null when no hold runs or the page does not know the prop.
   */
  public heldMark(): PageMark | null {
    return this.holding ? (this.marks?.byIndex.get(this.heldIndex) ?? null) : null;
  }

  /**
   * What a report shows of the store.
   *
   * @returns The indices hidden now and the bar's kind.
   */
  public reading(): HarvestReading {
    return { hidden: [...this.hidden].sort((a, b) => a - b), bar: this.snapshot.bar.kind };
  }

  /**
   * Takes the booted map's marks. Nothing is hidden yet: the server's lists say what is.
   *
   * @param marks The map's harvestable props.
   */
  public useWorld(marks: HarvestMarks): void {
    this.reset();
    this.marks = marks;
  }

  /**
   * Lets the map go: every prop hidden is drawn again and the marks are forgotten.
   */
  public clearWorld(): void {
    this.reset();
    this.marks = null;
  }

  /**
   * Whether a prop is gone for now.
   *
   * @param n The prop's index.
   * @returns True while it is hidden.
   */
  public isHidden(n: number): boolean {
    return this.hidden.has(n);
  }

  /**
   * Takes a chunk's `harvested` list (PROTOCOL.md 5.22): the chunk's props named are hidden, the others drawn, at once.
   * A chunk that holds no harvestable prop is passed over.
   *
   * @param cx The chunk's column.
   * @param cy Its row.
   * @param indices The harvested props the server lists.
   */
  public receiveList(cx: number, cy: number, indices: readonly number[]): void {
    const props = this.marks?.byChunk.get(chunkKey(cx, cy));
    if (props === undefined) {
      return;
    }
    const named = new Set(indices);
    for (const mark of props) {
      this.set(mark, named.has(mark.n), 0);
    }
  }

  /**
   * Takes one `harvestEvent` entry (PROTOCOL.md 5.23): a prop harvested fades out, one grown back fades in. A code this
   * build does not know and a prop it does not hold are passed over.
   *
   * @param index The prop's index.
   * @param code The event code.
   */
  public receiveEvent(index: number, code: number): void {
    const mark = this.marks?.byIndex.get(index);
    if (mark === undefined || (code !== HarvestEventCode.Harvested && code !== HarvestEventCode.Regrown)) {
      return;
    }
    this.set(mark, code === HarvestEventCode.Harvested, FADE_MS);
  }

  /**
   * Takes a `harvestState`: a hold starts the cast bar for the ticks the server named; the end clears it; a break shows
   * its interrupted look a moment and says why on the message line; a refusal says why there.
   *
   * @param event The state.
   */
  public receiveState(event: HarvestStateEvent): void {
    if (event.state === "hold") {
      const now = this.port.now();
      this.clear();
      this.heldIndex = event.index;
      this.change({ kind: "hold", startedMs: now, endsMs: now + Math.max(0, event.endsTick - this.port.tick()) * TICK_MS });
      return;
    }
    if (event.state === "done") {
      this.clear();
      this.change({ kind: "none" });
      return;
    }
    if (event.state === "broken") {
      this.clear();
      const until = this.port.now() + INTERRUPTED_MS;
      this.change({ kind: "interrupted", untilMs: until });
      this.clearTimer = setTimeout(() => {
        this.clearTimer = null;
        if (this.snapshot.bar.kind === "interrupted") {
          this.change({ kind: "none" });
        }
      }, INTERRUPTED_MS);
      this.port.say(brokenLine(event.reason));
      return;
    }
    this.port.say(refusedLine(event.reason));
  }

  /**
   * Asks the map server to harvest a prop. Nothing is sent while a hold runs.
   *
   * @param n The prop's index.
   * @returns Whether a request was sent.
   */
  public start(n: number): boolean {
    return !this.holding && this.port.send(n);
  }

  /**
   * Forgets everything the session told: every prop hidden is drawn again at once and the bar clears. The map's marks
   * stay, for the next session's lists.
   */
  public reset(): void {
    this.clear();
    for (const n of this.hidden) {
      const mark = this.marks?.byIndex.get(n);
      if (mark !== undefined) {
        this.port.withhold(mark.id, false, 0);
      }
    }
    if (this.hidden.size > 0) {
      this.moved += 1;
    }
    this.hidden.clear();
    if (this.snapshot.bar.kind !== "none") {
      this.change({ kind: "none" });
    }
  }

  /**
   * Hides or draws one prop, when that changes what it is.
   *
   * @param mark The prop.
   * @param hidden Whether it is gone.
   * @param fadeMs How long the change eases.
   */
  private set(mark: PageMark, hidden: boolean, fadeMs: number): void {
    if (this.hidden.has(mark.n) === hidden) {
      return;
    }
    if (hidden) {
      this.hidden.add(mark.n);
    } else {
      this.hidden.delete(mark.n);
    }
    this.moved += 1;
    this.port.withhold(mark.id, hidden, fadeMs);
  }

  /**
   * Cancels the timer that ends the interrupted look.
   */
  private clear(): void {
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
  }

  /**
   * Replaces the bar and tells the listeners.
   *
   * @param bar The new bar.
   */
  private change(bar: HarvestBarState): void {
    this.snapshot = { bar };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
